import _ from 'lodash'
import {
	Control,
	DENJI_SENSOR,
	DenjiHourSetting,
	Event,
	EventCustom,
	EventEx,
	EventFull,
	EventHeating,
	initEventHeating,
	isEventCustom,
	isEventEx,
	isEventHeating,
	MachineCategory,
	Scenario,
	State,
	SubControl,
	TimePattern,
	Trigger,
} from '../../types'
import { getControls } from '../ControlById/selectors'
import { getScenario } from '../ScenarioById/selectors'
import { getSubControl } from '../SubControlById/selectors'
import { getTrigger } from '../TriggerById/selectors'

export function getEvent(state: State, id: number): Event {
	return state.EventById[id]
}

export function getEventsByScenario(
	state: State,
	scenarioId: number,
): EventCustom[] {
	const scenario = getScenario(state, scenarioId)

	return getEventCustoms(state, scenario.eventIds)
}

export function getEvents(state: State, eventIds: number[]): Event[] {
	return eventIds.map((id) => getEvent(state, id))
}

export function getEventCustoms(state: State, eventIds: number[]) {
	return getEvents(state, eventIds).filter(isEventCustom)
}

export function getEventExs(state: State, eventIds: number[]) {
	const evs = getEvents(state, eventIds)

	return evs.filter(isEventEx)
}

export function getEventFulls(state: State, scenarioId: number): EventFull[] {
	const scenario = getScenario(state, scenarioId)

	if (!scenario) {
		return []
	}

	const events = getEvents(state, scenario.eventIds)
	const eventss = events.map((fe) => ({
		...fe,
		...(fe.triggerId ? { trigger: getTrigger(state, fe.triggerId) } : {}),
		subControl: getSubControl(state, fe.subControlId),
	}))

	return eventss
}

export function getReport(state: State): string {
	return JSON.stringify(state)
}

export function getEventEx(
	state: State,
	eventIds: number[],
	timePattern: TimePattern,
	subc: SubControl,
	init: EventEx,
): EventEx {
	const events = getEventExs(state, eventIds)

	return (
		events.find(
			(e) => subc.id === e.subControlId && e.timePattern === timePattern,
		) || init
	)
}

export function getEventHeating24(
	state: State,
	eventIds: number[],
	subc: SubControl,
) {
	const eventByHour: Record<number, EventHeating> = {}
	const events = getEvents(state, eventIds)
		.filter(isEventHeating)
		.filter((e) => e.subControlId === subc.id)

	events.forEach((ev) => {
		const [sh, sm] = ev.startTime.split(':').map(Number)
		const [eh, em] = ev.endTime.split(':').map(Number)

		const matchHourConfig = sm === 0 && em === 59 && sh === eh

		if (matchHourConfig) {
			eventByHour[sh] = ev
		}
	})

	for (let i = 0; i <= 23; i++) {
		if (!!eventByHour[i]) continue

		const hh = `${i}`.padStart(2, '0')

		eventByHour[i] = {
			...initEventHeating,
			startTime: `${hh}:00`,
			endTime: `${hh}:59`,
		}
	}
	return eventByHour
}

// 電磁弁 (denji) の 24時間設定を trigger から復元して hour→DenjiHourSetting に畳む。
// denji event は subControlId + hour窓(HH:00〜HH:59) で識別し、
// 紐づく trigger(sensor 13=土壌温度 low / 14=土壌湿度 high) から条件を復元する。
export function getDenjiHourSettings(
	state: State,
	eventIds: number[],
	subc: SubControl,
): Record<number, DenjiHourSetting> {
	const eventByHour: Record<number, Event> = {}
	const events = getEvents(state, eventIds).filter(
		(e) => e.subControlId === subc.id && e.timePattern === null,
	)

	events.forEach((ev) => {
		if (ev.startTime === null || ev.endTime === null) return
		const [sh, sm] = ev.startTime.split(':').map(Number)
		const [eh, em] = ev.endTime.split(':').map(Number)
		if (sm === 0 && em === 59 && sh === eh) eventByHour[sh] = ev
	})

	const byHour: Record<number, DenjiHourSetting> = {}
	for (let h = 0; h <= 23; h++) {
		const hh = `${h}`.padStart(2, '0')
		const ev = eventByHour[h]
		if (!ev) {
			byHour[h] = {
				hour: h,
				eventId: 0,
				enabled: false,
				soilTmpAbove: null,
				soilHmdBelow: null,
				startTime: `${hh}:00`,
				endTime: `${hh}:59`,
				triggerIds: [],
			}
			continue
		}
		const triggerIds = ev.triggerIds ?? []
		const triggers = triggerIds
			.map((id) => getTrigger(state, id))
			.filter((t): t is Trigger => !!t)
		const soilTmp = triggers.find((t) => t.sensor === DENJI_SENSOR.SOIL_TMP)
		const soilHmd = triggers.find((t) => t.sensor === DENJI_SENSOR.SOIL_HMD)
		byHour[h] = {
			hour: h,
			eventId: ev.id,
			enabled: ev.enabled,
			soilTmpAbove: soilTmp?.low ?? null,
			soilHmdBelow: soilHmd?.high ?? null,
			// この event は HH:00〜HH:59 の hour 窓（上の forEach で保証済み）
			startTime: `${hh}:00`,
			endTime: `${hh}:59`,
			triggerIds,
		}
	}
	return byHour
}

// throw しない active subcontrol lookup（gantt で全 control を安全に走査するため）
function findActiveSub(state: State, control: Control): SubControl | undefined {
	return control.subControlIds
		.map((id) => getSubControl(state, id))
		.find((sub) => sub.rate === 100)
}

export type ScenarioGanttRow = {
	controlId: number
	label: string
	category: MachineCategory
	hours: boolean[] // 長さ24。その時間帯に ON になり得るか
}

// シナリオ内の「時間割り当てされている機器」を 24時間のON/OFFに畳んで返す。
// hour ベースで表現できる heating / denji を対象（fan/mist 等の timePattern 系は対象外）。
export function getScenarioGanttRows(
	state: State,
	scenario: Scenario,
): ScenarioGanttRow[] {
	const controls = getControls(state, scenario.houseId)
	const rows: ScenarioGanttRow[] = []

	controls.forEach((control) => {
		const subc = findActiveSub(state, control)
		if (!subc) return

		if (control.category === 'heating') {
			const byHour = getEventHeating24(state, scenario.eventIds, subc)
			const hours = _.range(24).map((h) => byHour[h].enabled)
			if (hours.some(Boolean)) {
				rows.push({
					controlId: control.id,
					label: control.label,
					category: 'heating',
					hours,
				})
			}
		} else if (control.category === 'denji') {
			const byHour = getDenjiHourSettings(state, scenario.eventIds, subc)
			const hours = _.range(24).map((h) => {
				const s = byHour[h]
				return s.enabled && (s.soilTmpAbove !== null || s.soilHmdBelow !== null)
			})
			if (hours.some(Boolean)) {
				rows.push({
					controlId: control.id,
					label: control.label,
					category: 'denji',
					hours,
				})
			}
		}
	})

	return rows
}
